import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdArrowBack } from "react-icons/io";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useDeleteFeedbackMutation,
  useGetSingleFeedbackMutation,
  useToggleFeedbackVoteMutation,
} from "../../../redux/slices/feedbackApiSlice";
import { CustomLoader } from "../../../shared/components/others";
import { FeedbackForm } from "../components";
import { categories, statusMap, typesMap } from "../helper";

const FeedbackPage = () => {
  const { t } = useTranslation();

  const { id } = useParams();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state?.auth.userInfo);

  const [getFeedback, { isLoading }] = useGetSingleFeedbackMutation();
  const [deleteFeedback, { isLoading: deleteLoading }] =
    useDeleteFeedbackMutation();

  const [toggleVote] = useToggleFeedbackVoteMutation();
  const [feedback, setFeedback] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [editedFeedback, setEditedFeedback] = useState(null);

  const handleGetSingle = async (id) => {
    try {
      if (id) {
        const res = await getFeedback({ feedbackId: id }).unwrap();
        setFeedback(res);
        setEditedFeedback(res);
      }
    } catch (error) {}
  };

  const handleDelete = async (id) => {
    try {
      if (id) {
        const fb_category = feedback.category;
        await deleteFeedback({ feedbackId: id }).unwrap();
        toast.success("Votre feedback a bien été supprimé !");
        navigate(`/feedbacks?category=${fb_category}`);
      }
    } catch (error) {}
  };

  const handleVote = async (id) => {
    if (!userInfo || !feedback) return;

    try {
      await toggleVote({ feedbackId: id }).unwrap();

      setFeedback((prevFeedback) => {
        if (!prevFeedback) return prevFeedback;

        const updatedVoters = { ...prevFeedback.voters };
        let updatedLikes = prevFeedback.likes;

        if (updatedVoters[userInfo.user_id]) {
          // ✅ User is removing their vote
          delete updatedVoters[userInfo.user_id];
          updatedLikes -= 1;
        } else {
          // ✅ User is adding their vote
          updatedVoters[userInfo.user_id] = userInfo.username;
          updatedLikes += 1;
        }

        return { ...prevFeedback, voters: updatedVoters, likes: updatedLikes };
      });
    } catch (error) {
      console.error("Error toggling vote: ", error);
    }
  };

  useEffect(() => {
    if (id) {
      handleGetSingle(id);
    }
  }, [id]);

  useEffect(() => {
    if (feedback) {
      setEditedFeedback({
        id: feedback.id,
        type: feedback.type,
        title: feedback.title,
        text: feedback.text,
      });
    }
  }, [feedback]);

  if (isLoading)
    return (
      <div className="flex justify-center">
        <CustomLoader />
      </div>
    );
  if (!feedback) return <p>Feedback introuvable.</p>;

  const status = statusMap[feedback.status] || {
    title: "Inconnu",
    color: "bg-gray-400",
  };

  return (
    <div className="flex gap-6 p-6">
      {/* Sidebar: List of voters */}
      <div className="w-1/3">
        <div
          className="flex items-center gap-2 text-blue-500 cursor-pointer hover:underline mb-4"
          onClick={() =>
            navigate(`/feedbacks/list?category=${feedback.category}`)
          }
        >
          <IoMdArrowBack size={18} />
          <span>Retourner aux {categories[feedback.category]} </span>
        </div>
        <div className="bg-white rounded-lg border border-black/30">
          <div className="flex justify-between items-center p-4 border-b border-black/30">
            <h3 className="text-lg font-semibold">Votants</h3>
            <button
              className={`border-solid border-black/30 px-3 py-1 rounded-lg ${
                feedback?.voters[userInfo?.user_id]
                  ? "bg-black/10 hover:bg-black/20"
                  : "bg-white hover:bg-black/5"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleVote(feedback.id);
              }}
              disabled={userInfo ? false : true}
            >
              👍 {feedback.likes}
            </button>
          </div>
          <ul className="p-4">
            {feedback.likes > 0 ? (
              Object.values(feedback?.voters || {}).map((voter, index) => (
                <li key={index} className="py-1 border-b last:border-none">
                  {voter}
                </li>
              ))
            ) : (
              <div className="text-center text-gray-500">
                Aucun votant pour le moment
              </div>
            )}
          </ul>
        </div>
      </div>

      {/* Feedback Details or Edit Form */}
      <div className="w-2/3">
        {userInfo?.user_id === feedback.user_id && !isEditing && (
          <div className="flex justify-end gap-2 top-3 right-3 mb-2">
            <button
              className="bg-black/10 hover:bg-black/20 px-3 py-1 rounded-md"
              onClick={() => setIsEditing(true)}
            >
              Modifier
            </button>
            <button
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
              disabled={
                Object.keys(feedback.voters).length > 1 ||
                (Object.keys(feedback.voters).length === 1 &&
                  Object.keys(feedback.voters)[0] !== userInfo.user_id)
              }
              onClick={() => setOpen(true)}
            >
              Supprimer
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg border border-black/30">
          {isEditing ? (
            <FeedbackForm
              feedbackData={editedFeedback}
              setFeedbackData={setEditedFeedback}
              btnText="Modifier le feedback"
              setFeedback={setFeedback}
              setIsEditing={setIsEditing}
            />
          ) : (
            <div className="p-6">
              {/* HEADER */}
              <div className="flex items-center space-x-3 mb-1">
                <h1 className="text-2xl font-bold">{feedback.title}</h1>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                <span
                  className={`px-2 py-1 rounded-full text-white ${status.color}`}
                >
                  {status.title}
                </span>
                <span>
                  • Mis à jour le{" "}
                  {new Date(feedback.status_changed_at).toLocaleDateString()}
                </span>
              </div>

              {/* CONTENT */}
              <p className="text-gray-700 whitespace-pre-line">
                {feedback.text}
              </p>

              {/* FOOTER */}
              <div className="mt-4 flex justify-between">
                <p className="text-xs text-black/50">
                  {feedback.is_internal
                    ? "🏗 Ajouté par l'équipe"
                    : "📌 Proposé par la communauté"}
                </p>
                <p className="text-xs italic capital uppercase text-black/50">
                  {typesMap[feedback.type].cardText}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        className="relative z-[999]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-[999] w-screen overflow-y-auto bg-black/50">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-2xl sm:p-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  <div className="flex items-center gap-3">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-error/30 sm:mx-0 sm:h-10 sm:w-10">
                      <PriorityHighIcon
                        aria-hidden="true"
                        className="h-8 w-8 text-error"
                      />
                    </div>
                    <p className="text-base font-semibold leading-6 text-black/40">
                      {t("delete-feedback", "Supprimer le feedback")}
                    </p>
                  </div>
                </DialogTitle>
                <p className="text-base text-black text-left mt-5">
                  Êtes-vous sûr de vouloir supprimer votre feedback ? Les
                  données seront effacées et vous ne pourrez plus les récupérer.
                </p>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => handleDelete(feedback.id)}
                    className="inline-flex w-full justify-center rounded-md bg-error px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  >
                    Supprimer
                  </button>
                  <button
                    type="button"
                    data-autofocus
                    onClick={() => setOpen(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default FeedbackPage;
